/**
 * Product Description Widget
 * Displays product description with intelligent deduplication and Read More functionality
 *
 * Features:
 * - Shows full product description
 * - Detects if Features Showcase widget is present
 * - Strips out Features Showcase content to avoid duplicate content for SEO
 * - "Read More" collapse functionality for long descriptions
 * - Clean, readable formatting
 * - Theme token styling with British English
 * - Robust error handling for complex HTML
 *
 * Content Stripping Logic:
 * - Removes cross-sell cards (class="rrx-xsell")
 * - Removes <h3>Standout Features</h3> section and ALL content until next h3/h4 heading
 * - Removes all <h4> headings and their associated <li> lists (technical specs)
 * - Preserves all other content (paragraphs, other headings, images, etc.)
 *
 * @version 1.3.0
 */

(function(window) {
  'use strict';

  class ProductDescriptionWidget extends window.ProductWidgets.BaseWidget {
    constructor(manager, container, config) {
      super(manager, container, config);

      // Widget state
      this.productData = {
        description: config.productDescription || manager.productData.description || '',
        title: config.productTitle || manager.productData.title || ''
      };

      this.stripFeatures = config.stripFeatures !== false; // Default true
      this.processedDescription = '';
      this.isExpanded = false;
      this.maxLines = 8; // Show 8 lines before "Read More"
      this.toggleButtonClickHandler = null; // Store reference for cleanup
    }

    /**
     * Initialise widget - process description HTML
     */
    async init() {
      try {
        // Process description
        if (this.stripFeatures) {
          this.processedDescription = this.stripFeaturesContent(this.productData.description);
        } else {
          this.processedDescription = this.productData.description;
        }

      } catch (error) {
        console.error('[ProductDescriptionWidget] Initialisation failed:', error);
        // Fallback to showing original description if processing fails
        this.processedDescription = this.productData.description;
      }
    }

    /**
     * Strip Features Showcase content from description
     * Removes:
     * - Cross-sell cards (RiiRoo cross-link cards)
     * - <h3>Standout Features</h3> and ALL content until next h3/h4
     * - All <h4> headings and ALL content until next h3/h4
     * @param {string} html - Raw HTML from product description
     * @returns {string} - Cleaned HTML
     */
    stripFeaturesContent(html) {
      if (!html) {
        return '';
      }

      try {
        // Create a wrapper div to preserve structure
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;

        // Step 1: Remove cross-sell cards
        this.removeCrossSellCards(wrapper);

        // Step 2: Remove standalone style tags
        this.removeStandaloneTags(wrapper, 'style');

        // Step 3: Remove Standout Features section
        this.removeStandoutFeaturesSection(wrapper);

        // Step 4: Remove all h4 sections (technical specs)
        this.removeAttributeSections(wrapper);

        // Return cleaned HTML
        return wrapper.innerHTML;

      } catch (error) {
        console.error('[ProductDescriptionWidget] Error stripping features:', error);
        // Return original HTML if stripping fails
        return html;
      }
    }

    /**
     * Remove cross-sell cards (RiiRoo cross-link cards with class rrx-xsell)
     * @param {HTMLElement} container - Container element
     */
    removeCrossSellCards(container) {
      try {
        // Remove all elements with class "rrx-xsell"
        const crossSellCards = container.querySelectorAll('.rrx-xsell');
        crossSellCards.forEach(card => {
          try {
            if (card && card.parentNode) {
              card.parentNode.removeChild(card);
            }
          } catch (e) {
            console.warn('[ProductDescriptionWidget] Failed to remove cross-sell card:', e);
          }
        });

      } catch (error) {
        console.error('[ProductDescriptionWidget] Error removing cross-sell cards:', error);
      }
    }

    /**
     * Remove standalone tags (like orphaned style tags)
     * @param {HTMLElement} container - Container element
     * @param {string} tagName - Tag name to remove
     */
    removeStandaloneTags(container, tagName) {
      try {
        // Find all tags
        const tags = container.querySelectorAll(tagName);

        tags.forEach(tag => {
          try {
            // Only remove if it's not inside another meaningful element
            // Check if parent is just a p tag or the wrapper itself
            const parent = tag.parentNode;
            if (parent && (parent.tagName.toLowerCase() === 'p' || parent === container)) {
              // Also remove the parent p tag if it only contains this style tag
              if (parent.tagName.toLowerCase() === 'p' && parent.childNodes.length === 1) {
                parent.parentNode.removeChild(parent);
              } else {
                tag.parentNode.removeChild(tag);
              }
            }
          } catch (e) {
            console.warn('[ProductDescriptionWidget] Failed to remove tag:', e);
          }
        });

      } catch (error) {
        console.error('[ProductDescriptionWidget] Error removing standalone tags:', error);
      }
    }

    /**
     * Remove <h3>Standout Features</h3> section and ALL content until next h3/h4
     * @param {HTMLElement} container - Container element
     */
    removeStandoutFeaturesSection(container) {
      try {
        // Find h3 containing "Standout Features"
        const headings = Array.from(container.querySelectorAll('h3'));
        const standoutHeading = headings.find(h =>
          h.textContent.toLowerCase().includes('standout') &&
          h.textContent.toLowerCase().includes('feature')
        );

        if (!standoutHeading) {
          return; // No standout features section
        }

        // Collect all nodes to remove
        const nodesToRemove = [standoutHeading];
        let currentNode = standoutHeading.nextSibling;

        while (currentNode) {
          // If it's an element node, check if it's a heading
          if (currentNode.nodeType === Node.ELEMENT_NODE) {
            const tagName = currentNode.tagName.toLowerCase();

            // Stop if we hit another major heading
            if (tagName === 'h3' || tagName === 'h4') {
              break;
            }
          }

          // Mark this node for removal
          const nodeToRemove = currentNode;
          currentNode = currentNode.nextSibling;
          nodesToRemove.push(nodeToRemove);
        }

        // Remove all marked nodes
        nodesToRemove.forEach(node => {
          try {
            if (node && node.parentNode) {
              node.parentNode.removeChild(node);
            }
          } catch (e) {
            console.warn('[ProductDescriptionWidget] Failed to remove node:', e);
          }
        });

      } catch (error) {
        console.error('[ProductDescriptionWidget] Error removing standout features:', error);
      }
    }

    /**
     * Remove all <h4> headings and ALL content until next h3/h4
     * This removes technical specifications that are shown in Features Showcase
     * @param {HTMLElement} container - Container element
     */
    removeAttributeSections(container) {
      try {
        const h4Headings = Array.from(container.querySelectorAll('h4'));

        h4Headings.forEach(heading => {
          try {
            // Collect all nodes to remove (heading + all content until next heading)
            const nodesToRemove = [heading];
            let currentNode = heading.nextSibling;

            while (currentNode) {
              // If it's an element node, check if it's a heading
              if (currentNode.nodeType === Node.ELEMENT_NODE) {
                const tagName = currentNode.tagName.toLowerCase();

                // Stop if we hit another heading
                if (tagName === 'h3' || tagName === 'h4') {
                  break;
                }
              }

              // Mark this node for removal
              const nodeToRemove = currentNode;
              currentNode = currentNode.nextSibling;
              nodesToRemove.push(nodeToRemove);
            }

            // Remove all marked nodes
            nodesToRemove.forEach(node => {
              try {
                if (node && node.parentNode) {
                  node.parentNode.removeChild(node);
                }
              } catch (e) {
                console.warn('[ProductDescriptionWidget] Failed to remove node:', e);
              }
            });

          } catch (error) {
            console.warn('[ProductDescriptionWidget] Error processing h4 heading:', error);
          }
        });

      } catch (error) {
        console.error('[ProductDescriptionWidget] Error removing attribute sections:', error);
      }
    }

    /**
     * Check if content needs "Read More" functionality
     * @param {HTMLElement} contentElement - The content container
     * @returns {boolean}
     */
    needsReadMore(contentElement) {
      try {
        // Create a temporary element to measure height
        const temp = contentElement.cloneNode(true);
        temp.style.position = 'absolute';
        temp.style.visibility = 'hidden';
        temp.style.height = 'auto';
        temp.style.maxHeight = 'none';
        temp.style.display = 'block';
        temp.classList.add('is-expanded'); // Remove line clamp
        document.body.appendChild(temp);

        const fullHeight = temp.scrollHeight;
        const lineHeight = parseFloat(window.getComputedStyle(temp).lineHeight) || 24;
        const maxHeight = lineHeight * this.maxLines;

        document.body.removeChild(temp);

        return fullHeight > maxHeight + 20; // Add small buffer

      } catch (error) {
        console.error('[ProductDescriptionWidget] Error checking read more:', error);
        // Default to not showing read more if check fails
        return false;
      }
    }

    /**
     * Toggle expanded/collapsed state
     */
    toggleExpanded() {
      const wasExpanded = this.isExpanded;
      this.isExpanded = !this.isExpanded;
      this.updateExpandedState();

      // When collapsing, scroll back to widget top if user scrolled past it
      if (wasExpanded && !this.isExpanded) {
        this.scrollToWidgetTop();
      }
    }

    /**
     * Scroll to widget top when collapsing
     */
    scrollToWidgetTop() {
      // Use the container element directly
      const widgetTop = this.container.getBoundingClientRect().top + window.scrollY;

      // Scroll to widget with some offset for header
      window.scrollTo({
        top: Math.max(0, widgetTop - 80),
        behavior: 'smooth'
      });
    }

    /**
     * Update the UI based on expanded state
     */
    updateExpandedState() {
      try {
        const content = this.container.querySelector('.pw-product-description__content');
        const button = this.container.querySelector('.pw-product-description__toggle');
        const buttonText = button?.querySelector('.pw-toggle-text');
        const buttonIcon = button?.querySelector('.pw-toggle-icon');

        if (!content || !button) return;

        if (this.isExpanded) {
          content.classList.add('is-expanded');
          if (buttonText) buttonText.textContent = 'Read Less';
          if (buttonIcon) buttonIcon.innerHTML = this.getChevronUpIcon();
        } else {
          content.classList.remove('is-expanded');
          if (buttonText) buttonText.textContent = 'Read More';
          if (buttonIcon) buttonIcon.innerHTML = this.getChevronDownIcon();
        }

      } catch (error) {
        console.error('[ProductDescriptionWidget] Error updating expanded state:', error);
      }
    }

    /**
     * Get chevron down icon SVG
     */
    getChevronDownIcon() {
      return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }

    /**
     * Get chevron up icon SVG
     */
    getChevronUpIcon() {
      return `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 10L8 6L4 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    }

    /**
     * Render the widget using progressive enhancement
     * Instead of replacing innerHTML, enhance existing DOM content
     */
    async render() {
      try {
        // Find the base content rendered by Liquid
        const baseContent = this.container.querySelector('.pw-content-base');

        if (!baseContent) {
          // Fallback to old method if base content not found
          return this.renderLegacy();
        }

        // Get the content element from base content
        let contentWrapper = baseContent.querySelector('.pw-product-description');
        let contentElement = baseContent.querySelector('.pw-product-description__content');

        if (!contentElement) {
          this.container.style.display = 'none';
          return;
        }

        // Apply feature stripping directly to DOM if needed
        if (this.stripFeatures) {
          this.stripFeaturesFromDOM(contentElement);
        }

        // Check if content is empty after processing
        if (contentElement.textContent.trim() === '') {
          this.container.style.display = 'none';
          return;
        }

        // Create Read More button
        const toggleButton = document.createElement('button');
        toggleButton.className = 'pw-product-description__toggle';
        toggleButton.type = 'button';
        toggleButton.innerHTML = `
          <span class="pw-toggle-text">Read More</span>
          <span class="pw-toggle-icon">${this.getChevronDownIcon()}</span>
        `;

        // Add button to content wrapper
        contentWrapper.appendChild(toggleButton);

        // Set max-lines attribute for CSS
        contentElement.setAttribute('data-max-lines', this.maxLines);

        // Remove skeleton and base content elements
        const skeleton = this.container.querySelector('.pw-skeleton-content');
        if (skeleton) skeleton.remove();
        baseContent.style.display = 'block';
        baseContent.classList.remove('pw-content-base');

        // Remove skeleton class and add loaded class
        this.container.classList.remove('pw-skeleton-widget');
        this.container.classList.add('pw-widget--loaded');

        // Check if we need "Read More" functionality
        if (this.needsReadMore(contentElement)) {
          // Show the toggle button
          toggleButton.style.display = 'flex';

          // Create and store the click handler for proper cleanup
          this.toggleButtonClickHandler = () => this.toggleExpanded();
          toggleButton.addEventListener('click', this.toggleButtonClickHandler);
        } else {
          // Hide the toggle button and show all content
          toggleButton.style.display = 'none';
          contentElement.classList.add('is-expanded');
        }

      } catch (error) {
        console.error('[ProductDescriptionWidget] Render failed:', error);
        // Fallback to showing content without enhancement
        this.renderFallback();
      }
    }

    /**
     * Strip features content directly from DOM elements
     * @param {HTMLElement} contentElement - The content element to process
     */
    stripFeaturesFromDOM(contentElement) {
      try {
        // Remove cross-sell cards
        const crossSellCards = contentElement.querySelectorAll('.rrx-xsell');
        crossSellCards.forEach(card => card.remove());

        // Remove "Standout Features" section and all content until next h3/h4
        const headings = contentElement.querySelectorAll('h3, h4');
        headings.forEach(heading => {
          if (heading.tagName === 'H3' && heading.textContent.includes('Standout Features')) {
            // Remove this heading and all siblings until next h3/h4
            let currentElement = heading.nextElementSibling;
            heading.remove();
            while (currentElement && !['H3', 'H4'].includes(currentElement.tagName)) {
              const next = currentElement.nextElementSibling;
              currentElement.remove();
              currentElement = next;
            }
          } else if (heading.tagName === 'H4') {
            // Remove all h4 sections (technical specs)
            let currentElement = heading.nextElementSibling;
            heading.remove();
            while (currentElement && !['H3', 'H4'].includes(currentElement.tagName)) {
              const next = currentElement.nextElementSibling;
              currentElement.remove();
              currentElement = next;
            }
          }
        });
      } catch (error) {
        console.error('[ProductDescriptionWidget] Error stripping features from DOM:', error);
      }
    }

    /**
     * Legacy render method (fallback)
     */
    async renderLegacy() {
      try {
        // If no description, don't render anything
        if (!this.processedDescription || this.processedDescription.trim() === '') {
          this.container.style.display = 'none';
          return;
        }

        // Build HTML
        const html = `
          <div class="pw-product-description">
            <div class="pw-product-description__content rte" data-max-lines="${this.maxLines}">
              ${this.processedDescription}
            </div>
            <button class="pw-product-description__toggle" type="button">
              <span class="pw-toggle-text">Read More</span>
              <span class="pw-toggle-icon">${this.getChevronDownIcon()}</span>
            </button>
          </div>
        `;

        // Insert HTML
        this.container.innerHTML = html;

        // Remove skeleton class to hide skeleton loading state
        this.container.classList.remove('pw-skeleton-widget');
        // Add loaded class to show actual content
        this.container.classList.add('pw-widget--loaded');

        // Check if we need "Read More" functionality
        const contentElement = this.container.querySelector('.pw-product-description__content');
        const toggleButton = this.container.querySelector('.pw-product-description__toggle');

        if (this.needsReadMore(contentElement)) {
          // Show the toggle button
          toggleButton.style.display = 'flex';

          // Create and store the click handler for proper cleanup
          this.toggleButtonClickHandler = () => this.toggleExpanded();
          toggleButton.addEventListener('click', this.toggleButtonClickHandler);
        } else {
          // Hide the toggle button and show all content
          toggleButton.style.display = 'none';
          contentElement.classList.add('is-expanded');
        }

      } catch (error) {
        console.error('[ProductDescriptionWidget] Legacy render failed:', error);
        this.renderFallback();
      }
    }

    /**
     * Fallback render - minimal processing
     */
    renderFallback() {
      try {
        const baseContent = this.container.querySelector('.pw-content-base');
        if (baseContent) {
          baseContent.style.display = 'block';
          const skeleton = this.container.querySelector('.pw-skeleton-content');
          if (skeleton) skeleton.remove();
        }
        this.container.classList.remove('pw-skeleton-widget');
        this.container.classList.add('pw-widget--loaded');
      } catch (fallbackError) {
        console.error('[ProductDescriptionWidget] Fallback render failed:', fallbackError);
      }
    }

    /**
     * Clean up widget
     */
    async destroy() {
      try {
        // Remove event listeners properly using stored reference
        const toggleButton = this.container.querySelector('.pw-product-description__toggle');
        if (toggleButton && this.toggleButtonClickHandler) {
          toggleButton.removeEventListener('click', this.toggleButtonClickHandler);
        }

        this.container.innerHTML = '';

      } catch (error) {
        console.error('[ProductDescriptionWidget] Error during destroy:', error);
      }
    }
  }

  // Register widget class
  if (!window.ProductWidgets) {
    window.ProductWidgets = { widgets: {} };
  }
  if (!window.ProductWidgets.widgets) {
    window.ProductWidgets.widgets = {};
  }

  window.ProductWidgets.widgets.ProductDescriptionWidget = ProductDescriptionWidget;

})(window);
